import { useEffect, useRef, useState } from "react";
import SectionLabel from "./SectionLabel";

type Stat = {
  value: string;
  numeric: number;
  label: string;
  size: number;
  bg: string;
  color: string;
  ring: string;
  spin: number;
  idleAmp: number;
  idleDur: number;
  idleDelay: number;
  parallax: number;
};

const STATS: Stat[] = [
  {
    value: "50",
    numeric: 50,
    label: "Utility apps launched since 2020",
    size: 400,
    bg: "#93C5FD",
    color: "#0B0B0B",
    ring: "rgba(147,197,253,0.5)",
    spin: -18,
    idleAmp: 10,
    idleDur: 7.5,
    idleDelay: 0,
    parallax: 1,
  },
  {
    value: "12",
    numeric: 12,
    label: "Million total app installs worldwide",
    size: 260,
    bg: "#60A5FA",
    color: "#0B1B4D",
    ring: "rgba(96,165,250,0.5)",
    spin: 22,
    idleAmp: 14,
    idleDur: 6.2,
    idleDelay: 1.1,
    parallax: 1.6,
  },
  {
    value: "30",
    numeric: 30,
    label: "Countries with active users",
    size: 320,
    bg: "#2563EB",
    color: "#ECECEC",
    ring: "rgba(37,99,235,0.55)",
    spin: -14,
    idleAmp: 12,
    idleDur: 8.4,
    idleDelay: 0.6,
    parallax: 1.8,
  },
  {
    value: "40",
    numeric: 40,
    label: "Specialists — developers, marketers, designers & QA",
    size: 340,
    bg: "#1E40AF",
    color: "#ECECEC",
    ring: "rgba(30,64,175,0.55)",
    spin: 12,
    idleAmp: 11,
    idleDur: 7.8,
    idleDelay: 1.4,
    parallax: 1.2,
  },
];

function useSectionProgress(ref: React.RefObject<HTMLElement | null>) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let rafId = 0;

    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = rect.height + vh;
      const passed = vh - rect.top;
      const p = Math.min(1, Math.max(0, passed / total));
      setProgress(p);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref]);

  return progress;
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

export default function NumbersSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const progress = useSectionProgress(sectionRef);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  const enter = easeOut(Math.min(1, progress / 0.55));

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    setMouse({ x: mx, y: my });
  };

  const onMouseLeave = () => setMouse({ x: 0.5, y: 0.5 });

  const parallaxX = (mouse.x - 0.5) * 40;
  const parallaxY = (mouse.y - 0.5) * 40;

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{
        background: "#000",
        padding: "80px 16px 120px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      {/* Faint moving grid backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          transform: `translate3d(${-parallaxX * 0.4}px, ${
            -parallaxY * 0.4
          }px, 0)`,
          transition: "transform 0.5s ease-out",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 75%)",
        }}
      />

      {/* Mouse-follow soft spotlight */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at ${mouse.x * 100}% ${
            mouse.y * 100
          }%, rgba(147,197,253,0.08), transparent 60%)`,
          transition: "background 0.2s ease-out",
        }}
      />

      <div className="relative mx-auto" style={{ maxWidth: 1440 }}>
        <SectionLabel>Intellivex by the numbers</SectionLabel>

        {/* Desktop: absolute positioned bubbles */}
        <div
          ref={stageRef}
          className="hidden lg:block relative mt-16"
          style={{ height: 720, width: "100%" }}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          <ConnectionLines enter={enter} />
          <AmbientDot
            style={{ width: 22, height: 22, background: "#93C5FD", left: "36%", top: 90 }}
            enter={enter} idleAmp={6} idleDur={5.4} idleDelay={0.3}
            parallax={{ x: parallaxX * 1.4, y: parallaxY * 1.4 }}
          />
          <AmbientDot
            style={{ width: 34, height: 34, background: "#0B1B4D", border: "1px solid #1E3A8A", right: "18%", bottom: 60 }}
            enter={enter} idleAmp={9} idleDur={6.6} idleDelay={1.4}
            parallax={{ x: parallaxX * -1.6, y: parallaxY * -1.6 }}
          />
          <AmbientDot
            style={{ width: 16, height: 16, background: "#8A8A8A", left: "60%", top: 20 }}
            enter={enter} idleAmp={5} idleDur={4.8} idleDelay={0.8}
            parallax={{ x: parallaxX * 2.2, y: parallaxY * 2.2 }}
          />
          <AmbientDot
            style={{ width: 26, height: 26, background: "#93C5FD", left: "8%", bottom: 20 }}
            enter={enter} idleAmp={7} idleDur={7.2} idleDelay={2}
            parallax={{ x: parallaxX * -1.2, y: parallaxY * -1.2 }}
          />
          <OrbitingDots enter={enter} />

          {STATS.map((stat, i) => (
            <StatCircle
              key={stat.value}
              stat={stat}
              style={DESKTOP_POSITIONS[i]}
              enter={enter}
              delay={i * 0.14}
              index={i}
              parallax={{ x: parallaxX * stat.parallax, y: parallaxY * stat.parallax }}
            />
          ))}
        </div>

        {/* Mobile: grid layout */}
        <div className="lg:hidden mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {STATS.map((stat, i) => (
            <MobileStatCard key={stat.value} stat={stat} index={i} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes sg-idle-float {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50%      { transform: translate3d(0, calc(var(--sg-idle-amp, 10) * -1px), 0); }
        }
        @keyframes sg-pulse-ring {
          0%   { transform: scale(1);   opacity: 0.55; }
          80%  { transform: scale(1.6); opacity: 0;    }
          100% { transform: scale(1.6); opacity: 0;    }
        }
        @keyframes sg-shine-sweep {
          0%   { transform: translateX(-140%) skewX(-20deg); }
          100% { transform: translateX(180%)  skewX(-20deg); }
        }
        @keyframes sg-orbit-a {
          from { transform: rotate(0deg)   translateX(160px) rotate(0deg);   }
          to   { transform: rotate(360deg) translateX(160px) rotate(-360deg); }
        }
        @keyframes sg-orbit-b {
          from { transform: rotate(0deg)   translateX(240px) rotate(0deg);   }
          to   { transform: rotate(-360deg) translateX(240px) rotate(360deg); }
        }
        @keyframes sg-orbit-c {
          from { transform: rotate(0deg)   translateX(320px) rotate(0deg);   }
          to   { transform: rotate(360deg) translateX(320px) rotate(-360deg); }
        }
        @keyframes sg-blink {
          0%, 100% { opacity: 0.35; }
          50%      { opacity: 1;    }
        }
        @keyframes sg-dash {
          to { stroke-dashoffset: -40; }
        }
        @keyframes numFadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

/* ----------------------------------------------------------------------- */

const DESKTOP_POSITIONS: React.CSSProperties[] = [
  { left: "-2%", top: 220 },
  { left: "30%", top: 80 },
  { right: "-6%", top: 130 },
  { left: "55%", top: 380 },
];

function ConnectionLines({ enter }: { enter: number }) {
  const visible = Math.min(1, Math.max(0, (enter - 0.4) / 0.6));
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: visible * 0.35 }}
    >
      <line x1="10%" y1="52%" x2="42%" y2="22%" stroke="#93C5FD" strokeWidth={1} strokeDasharray="4 6" style={{ animation: "sg-dash 4s linear infinite" }} />
      <line x1="42%" y1="22%" x2="56%" y2="62%" stroke="#60A5FA" strokeWidth={1} strokeDasharray="4 6" style={{ animation: "sg-dash 6s linear infinite" }} />
      <line x1="56%" y1="62%" x2="88%" y2="36%" stroke="#2563EB" strokeWidth={1} strokeDasharray="4 6" style={{ animation: "sg-dash 5s linear infinite" }} />
      <line x1="20%" y1="65%" x2="55%" y2="75%" stroke="#1E40AF" strokeWidth={1} strokeDasharray="4 6" style={{ animation: "sg-dash 7s linear infinite" }} />
    </svg>
  );
}

function OrbitingDots({ enter }: { enter: number }) {
  if (enter < 0.9) return null;
  return (
    <div className="absolute pointer-events-none" style={{ left: "22%", top: "38%", width: 0, height: 0 }}>
      <span className="absolute block rounded-full" style={{ width: 8, height: 8, background: "#93C5FD", animation: "sg-orbit-a 14s linear infinite" }} />
      <span className="absolute block rounded-full" style={{ width: 6, height: 6, background: "#60A5FA", animation: "sg-orbit-b 22s linear infinite" }} />
      <span className="absolute block rounded-full" style={{ width: 5, height: 5, background: "#2563EB", animation: "sg-orbit-c 30s linear infinite" }} />
    </div>
  );
}

function AmbientDot({
  style, enter, idleAmp, idleDur, idleDelay, parallax,
}: {
  style: React.CSSProperties; enter: number; idleAmp: number; idleDur: number; idleDelay: number; parallax: { x: number; y: number };
}) {
  const scale = enter;
  return (
    <span
      className="absolute rounded-full"
      style={{
        ...style,
        transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0) scale(${scale})`,
        opacity: enter,
        transition: "transform 0.4s ease-out",
      }}
    >
      <span
        aria-hidden
        style={{
          display: "block", width: "100%", height: "100%", borderRadius: "9999px", background: "inherit", border: "inherit",
          "--sg-idle-amp": idleAmp,
          animation: enter > 0.9
            ? `sg-idle-float ${idleDur}s ease-in-out ${idleDelay}s infinite, sg-blink ${idleDur * 0.7}s ease-in-out ${idleDelay}s infinite`
            : "none",
        } as React.CSSProperties}
      />
    </span>
  );
}

function StatCircle({
  stat, style, enter, delay, index, parallax,
}: {
  stat: Stat; style: React.CSSProperties; enter: number; delay: number; index: number; parallax: { x: number; y: number };
}) {
  const [hovered, setHovered] = useState(false);
  const [burstKey, setBurstKey] = useState(0);

  const raw = (enter - delay) / (1 - delay);
  const t = Math.min(1, Math.max(0, raw));
  const eScale = easeOutBack(t);
  const e = easeOut(t);

  const growth = 0.04 + eScale * 0.96;
  const hoverScale = hovered ? 1.06 : 1;
  const scale = growth * hoverScale;
  const rotation = stat.spin * (1 - e);
  const opacity = e;

  const displayNum = Math.round(stat.numeric * e);
  const display = formatNumber(displayNum, stat.value);
  const showText = e > 0.55;

  return (
    <div
      className="absolute cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setBurstKey((k) => k + 1)}
      style={{
        width: stat.size, height: stat.size,
        transform: `translate3d(${parallax.x}px, ${parallax.y}px, 0)`,
        transition: "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
        willChange: "transform",
        ...style,
      }}
    >
      {e > 0.95 && (
        <>
          <span aria-hidden className="absolute inset-0 rounded-full pointer-events-none" style={{ border: `2px solid ${stat.ring}`, animation: `sg-pulse-ring ${4 + index * 0.4}s ease-out ${index * 0.5}s infinite` }} />
          <span aria-hidden className="absolute inset-0 rounded-full pointer-events-none" style={{ border: `1px solid ${stat.ring}`, animation: `sg-pulse-ring ${5 + index * 0.3}s ease-out ${index * 0.5 + 1.4}s infinite` }} />
        </>
      )}
      {burstKey > 0 && (
        <span key={burstKey} aria-hidden className="absolute inset-0 rounded-full pointer-events-none" style={{ border: `2px solid ${stat.ring}`, animation: "sg-pulse-ring 0.9s ease-out forwards" }} />
      )}
      <div
        className="relative w-full h-full rounded-full flex flex-col items-center justify-center text-center overflow-hidden"
        style={{
          background: stat.bg, color: stat.color, padding: 32,
          transform: `scale(${scale}) rotate(${rotation}deg)`, opacity,
          transition: "transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.35s ease-out",
          willChange: "transform",
          boxShadow: hovered
            ? `0 0 0 6px ${stat.ring}, 0 30px 80px -20px ${stat.ring}`
            : `0 0 0 0 ${stat.ring}, 0 0 0 -20px ${stat.ring}`,
        }}
      >
        <div
          className="relative flex flex-col items-center justify-center w-full h-full"
          style={{
            "--sg-idle-amp": stat.idleAmp,
            animation: e > 0.95
              ? `sg-idle-float ${stat.idleDur}s ease-in-out ${stat.idleDelay + index * 0.3}s infinite`
              : "none",
          } as React.CSSProperties}
        >
          <div style={{
            fontSize: Math.max(48, stat.size * 0.22), fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1,
            opacity: showText ? 1 : 0, transform: showText ? "translateY(0)" : "translateY(8px)",
            transition: "opacity 0.4s ease-out, transform 0.4s ease-out",
          }}>
            {display}
          </div>
          <div style={{
            marginTop: 12, fontSize: 13, fontWeight: 500, opacity: showText ? 0.85 : 0, maxWidth: stat.size * 0.68, lineHeight: 1.3,
            transform: showText ? "translateY(0)" : "translateY(6px)",
            transition: "opacity 0.5s ease-out 0.1s, transform 0.5s ease-out 0.1s",
          }}>
            {stat.label}
          </div>
        </div>
        {hovered && (
          <span aria-hidden className="absolute inset-0 pointer-events-none" style={{ overflow: "hidden", borderRadius: "9999px" }}>
            <span className="absolute top-0 left-0 h-full" style={{ width: "40%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)", animation: "sg-shine-sweep 1.1s ease-out forwards" }} />
          </span>
        )}
      </div>
    </div>
  );
}

function MobileStatCard({ stat, index }: { stat: Stat; index: number }) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }); },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        background: stat.bg,
        color: stat.color,
        padding: "28px 24px",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.7s ease ${index * 0.12}s, transform 0.7s ease ${index * 0.12}s`,
        animation: inView ? `numFloat ${stat.idleDur}s ease-in-out ${stat.idleDelay + index * 0.3}s infinite` : "none",
      }}
    >
      <div style={{ fontSize: "clamp(36px, 8vw, 56px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1 }}>
        {stat.value}
      </div>
      <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500, opacity: 0.85, lineHeight: 1.3 }}>
        {stat.label}
      </div>
      {/* Shine */}
      <span aria-hidden className="absolute inset-0 pointer-events-none" style={{ overflow: "hidden", borderRadius: 20 }}>
        <span style={{ position: "absolute", top: 0, left: "-100%", width: "40%", height: "100%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)", animation: "sg-shine-sweep 3s ease-out infinite" }} />
      </span>
      <style>{`
        @keyframes numFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes sg-shine-sweep {
          0%   { left: -100%; }
          100% { left: 200%; }
        }
      `}</style>
    </div>
  );
}

function formatNumber(n: number, original: string): string {
  if (original.includes(" ")) return n.toLocaleString("en-US").replace(/,/g, " ");
  return String(n);
}